import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2, CalendarCheck, AlertTriangle, Trash2 } from 'lucide-react';
import { addHourBalance, deleteHourBalance } from '../services/api';
import {
  COMPUTATION_START_REASON, COMPUTATION_START_LABEL, BALANCE_EPOCH_ISO,
  computationStartOffsets, offsetDayKey,
} from '../lib/hourBalance';
import { invalidateWorkerHourBalance } from '../hooks/useWorkerHourBalance';
import { isDayKey, timeZoneForWorker, todayInZone } from '../lib/workerTime';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';

// ── Fecha de inicio de cómputo de un trabajador (su "alta" para la bolsa) ──
// Desde esa fecha cuentan sus horas; todo lo anterior queda FUERA (ni jornada
// esperada, ni faltas, ni trabajado). Ver src/lib/hourBalance.js.
//
// El modelo Person del backend NO tiene campo de fecha, así que la guardamos
// como fila marcadora en /api/hourBalance:
//   { workerId, date, offset: 0, reason: 'Inicio de computo' }
// Editar = crear el marcador nuevo y BORRAR los antiguos (nunca se borra nada
// que no sea un marcador). Si quedara más de uno, manda el de fecha MÁS
// RECIENTE y este modal ofrece limpiar los sobrantes.
//
// Props:
//   open, worker, offsets (ajustes ya cargados por el padre), onClose,
//   onSaved(dayKey|null) → el padre recarga sus ajustes.
const ComputationStartModal = ({ open, worker, offsets = [], onClose, onSaved }) => {
  const toast = useToast();
  const tz = timeZoneForWorker(worker);
  const markers = useMemo(() => computationStartOffsets(offsets), [offsets]);
  const current = markers.length ? offsetDayKey(markers[0].date) : null;
  const duplicates = markers.slice(1);

  const [date, setDate] = useState(current || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDate(current || '');
    setError(null);
  }, [open, current]);

  if (!open) return null;

  // Borra marcadores (y SOLO marcadores). Devuelve cuántos fallaron.
  const deleteMarkers = async (rows) => {
    let failed = 0;
    for (const row of rows) {
      try {
        await deleteHourBalance(row.id);
      } catch {
        failed += 1;
      }
    }
    return failed;
  };

  const finish = (nextKey) => {
    invalidateWorkerHourBalance(worker?.id);
    onSaved?.(nextKey);
    onClose?.();
  };

  const handleSave = async () => {
    setError(null);
    if (!isDayKey(date)) {
      setError('Elige una fecha válida.');
      return;
    }
    if (date === current && duplicates.length === 0) {
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      // 1) Primero se crea el marcador nuevo: si el borrado falla después, el
      //    trabajador se queda con la fecha CORRECTA (gana la más reciente si
      //    es posterior) y nunca sin ninguna.
      if (date !== current) {
        await addHourBalance({
          workerId: worker.id, date, reason: COMPUTATION_START_REASON, offset: 0,
        });
      }
      // 2) Y luego se borran TODOS los marcadores anteriores.
      const stale = date !== current ? markers : duplicates;
      const failed = await deleteMarkers(stale);
      toast.success(FEEDBACK.computationStartSaved({ dayKey: date, workerName: worker?.name }));
      if (failed) toast.warning(FEEDBACK.computationStartStaleKept(failed));
      finish(date);
    } catch (err) {
      setError(err?.message || 'No se pudo guardar la fecha de inicio de cómputo.');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!markers.length) { onClose?.(); return; }
    if (!window.confirm('¿Quitar la fecha de inicio de cómputo? La bolsa volverá a contar desde el primer fichaje.')) return;
    setError(null);
    setSaving(true);
    try {
      const failed = await deleteMarkers(markers);
      if (failed === markers.length) {
        setError('No se pudo eliminar la fecha de inicio de cómputo.');
        return;
      }
      toast.success(FEEDBACK.computationStartCleared({ workerName: worker?.name }));
      if (failed) toast.warning(FEEDBACK.computationStartStaleKept(failed));
      finish(null);
    } finally {
      setSaving(false);
    }
  };

  const beforeEpoch = isDayKey(date) && date < BALANCE_EPOCH_ISO;
  const inFuture = isDayKey(date) && date > todayInZone(tz);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">Bolsa de horas</div>
            <h2 className="font-display text-lg font-bold text-ink truncate">{COMPUTATION_START_LABEL}</h2>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <p className="text-sm text-ink-muted">
            Desde esta fecha cuentan las horas de <b className="text-ink">{worker?.name || 'este trabajador'}</b>.
            Todo lo anterior queda fuera del cómputo: ni jornada esperada, ni faltas, ni horas trabajadas.
            Los fichajes anteriores se siguen viendo en el historial, marcados como “Fuera de cómputo”.
          </p>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
              Trabaja desde
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-primary text-sm transition-colors tabular-nums"
            />
            {beforeEpoch && (
              <div className="text-xs text-ink-muted mt-1">
                La bolsa nunca cuenta antes del {BALANCE_EPOCH_ISO.split('-').reverse().join('/')}, así que una fecha anterior no cambia nada.
              </div>
            )}
            {inFuture && (
              <div className="text-xs text-warning mt-1">
                Es una fecha futura: hasta que llegue, la bolsa de este trabajador quedará a 0.
              </div>
            )}
          </div>

          {duplicates.length > 0 && (
            <div className="border border-warning/30 bg-warning/5 rounded-lg p-3 text-xs text-ink flex items-start gap-2">
              <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
              <span>
                Hay {duplicates.length + 1} marcadores de inicio de cómputo. Manda el más reciente
                ({current}). Al guardar se limpian los sobrantes.
              </span>
            </div>
          )}

          {error && (
            <div className="border border-danger/30 bg-danger/5 rounded-lg p-3 text-sm text-danger flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleClear}
            disabled={saving || markers.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-danger/80 hover:text-danger disabled:opacity-40 disabled:hover:text-danger/80 rounded-lg transition"
            title="Quitar la fecha: la bolsa vuelve a contar desde el primer fichaje"
          >
            <Trash2 size={14} /> Quitar fecha
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-ink-muted hover:text-ink rounded-lg transition"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !isDayKey(date)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-dark disabled:opacity-50 rounded-lg transition"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CalendarCheck size={14} />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComputationStartModal;
